import React, { useContext } from "react";
import { getBeeswaxIntegrationFlipDate } from "./beeswaxIntegrationExperiment";
import { shouldEnableBrandHealthGoogleTab } from "./brandHealthGoogleTabExperiment";
import { shouldEnableBudgetIntakeTool } from "./budgetIntakeToolExperiement";
import { shouldEnableCreativeInsights } from "./creativeInsightsExperiment";
import { shouldEnableInsightsCategories } from "./insightsCategoryExperiment";
import { shouldEnableKpiSetup } from "./kpiSetupExperiment";
import {
  shouldEnableLinearCampaigns,
  shouldEnableAllLinearChangesUsers,
  shouldEnableAllLinearChangesCompanies,
  shouldEnableUpfrontBuyPacingUsers,
  shouldEnableCADLinearCompanies,
} from "./linearBuyingExperiment";
import { shouldEnableLinearDelivery } from "./linearDeliveryExperiment";
import { shouldEnableMarketChannelColumns } from "./marketChannelColumnsExperiment";
import { shouldEnableMasterTargeting } from "./masterTargetingExperiment";
import { shouldEnableMobiusIntegration } from "./mobiusIntegrationExperiment";
import { shouldEnableMobiusSlidesCompany } from "./mobiusSlidesExperiment";
import { shouldEnableBugTicket } from "./bugModalExperiment";
import { shouldEnableRadioBuying } from "./radioBuyingExperiment";
import { shouldEnableStreamingCreativesImporter } from "./streamingCreativesImporterExperiment";
import { shouldEnableStreamingGeoTargeting } from "./streamingGeoTargetingExperiment";
import { shouldEnableStreamingOptimizationUpdates } from "./streamingOptimizationExperiment";
import { shouldEnableTimeTravel } from "./timeTravelExperiment";
import { shouldNotDefaultHomeV2 } from "./homeV2Experiment";
import { shouldEnableStreamingNetworksUpdates } from "./streamingNetworksExperiment";
import { shouldEnableStreamingMonitoring } from "./streamingMonitoringExperiment";
import { shouldEnableMMMOfflineInputs } from "./mmmOfflineInputs";
import { shouldEnableMMMNavbarGui } from "./mmmNavbarGuiExperiment";
import { shouldEnableMetaBuying } from "./metaBuyingExperiment";
import { shouldEnableMetaBuyingTools } from "./metaBuyingToolsExperiment";
import { shouldEnableForecasting } from "./forecastingExperiment";
import { shouldEnableMarketingInputsCCData } from "./marketingInputsCCDataExperiment";

export const engineeringUsers = [
  "alex.echeverria@tinuiti.com",
  "automation@tinuiti.com",
  "benjamin.contesse@tinuiti.com",
  "berto.garciacarrillo@tinuiti.com",
  "brenna.huggins@tinuiti.com",
  "carling.sugarman@tinuiti.com",
  "garrett.cheadle@tinuiti.com",
  "hinling.yeung@tinuiti.com",
  "justin.manus@tinuiti.com",
  "katya.villano@tinuiti.com",
  "kyle.chan@tinuiti.com",
  "miguel.garcia@tinuiti.com",
  "nicole.yu@tinuiti.com",
  "skye.bishop@tinuiti.com",
  "vinnie.jaidar@tinuiti.com",
];

export const productUsers = [
  "micah.kilimann@tinuiti.com",
  "lauren.wolfen@tinuiti.com",
  "sean.odlum@tinuiti.com",
  "samuel.york@tinuiti.com",
  "jake.thomas@tinuiti.com",
  "ben.plantz@tinuiti.com",
];

export const econometricsUsers = [
  "philip.rundall@tinuiti.com",
  "doug.macdonald@tinuiti.com",
  "taurey.carr@tinuiti.com",
  "will.hammond@tinuiti.com",
  "galen.murray@tinuiti.com",
  "morgan.stockham@tinuiti.com",
  "paul.sims@tinuiti.com",
  "christopher.nilsson@tinuiti.com",
];
/*
  Experiment Context + hooks
  ----------------------------------------------------------------------------------------------------
  Used to set and access experiments throughout the app.
  */

export const ExperimentContext = React.createContext<Record<string, boolean | string>>({});

export const useExperimentContext = (): Record<string, boolean | string> => {
  const experiments = useContext(ExperimentContext);
  if (experiments === null) {
    throw new Error("You must wrap your components in a FeatureProvider.");
  }
  return experiments;
};

export const useExperimentFlag = (experimentName: string): boolean | string => {
  const experiments = useExperimentContext();
  return experiments[experimentName];
};

/*
  Experiment Setup
  ----------------------------------------------------------------------------------------------------
  Add experiment flags and call out to experiment-specific configs here!
  All logic for whether an experiment should be enabled should live in a separate file per experiment.
  */

export const getExperimentsForUser = (userEmail: string): Record<string, boolean | string> => {
  if (!userEmail) {
    return {};
  }

  return {
    enableAllLinearBuyingChanges: shouldEnableAllLinearChangesUsers(userEmail),
    enableBudgetIntakeTool: shouldEnableBudgetIntakeTool(userEmail),
    enableBugTicket: shouldEnableBugTicket(userEmail),
    enableKpiSetup: shouldEnableKpiSetup(userEmail),
    enableLinearDelivery: shouldEnableLinearDelivery(userEmail),
    enableMarketChannelColumns: shouldEnableMarketChannelColumns(userEmail),
    enableMasterTargeting: shouldEnableMasterTargeting(userEmail),
    enableMetaBuyingTools: shouldEnableMetaBuyingTools(userEmail),
    enableMMMOfflineInputs: shouldEnableMMMOfflineInputs(userEmail),
    enableMobiusIntegration: shouldEnableMobiusIntegration(userEmail),
    enableStreamingCreativesImporter: shouldEnableStreamingCreativesImporter(userEmail),
    enableStreamingGeoTargeting: shouldEnableStreamingGeoTargeting(userEmail),
    enableStreamingMonitoring: shouldEnableStreamingMonitoring(userEmail),
    enableStreamingNetworksUpdates: shouldEnableStreamingNetworksUpdates(userEmail),
    enableStreamingOptimizationUpdates: shouldEnableStreamingOptimizationUpdates(userEmail),
    enableTimeTravel: shouldEnableTimeTravel(userEmail),
    enableUpfrontLinearPacingView: shouldEnableUpfrontBuyPacingUsers(userEmail),
    enableForecasting: shouldEnableForecasting(userEmail),
    // Add new user-based experiment flags here like this:
    // myExperimentFlag: shouldEnableMyExperimentFlag(),
  };
};
export const getExperimentsForCompany = (company: string): Record<string, boolean | string> => {
  if (!company) {
    return {};
  }

  return {
    beeswaxIntegrationFlipDate: getBeeswaxIntegrationFlipDate(company),
    enableAllLinearBuyingChanges: shouldEnableAllLinearChangesCompanies(company),
    enableBeeswaxIntegration: Boolean(getBeeswaxIntegrationFlipDate(company)),
    enableCADLinearCompanies: shouldEnableCADLinearCompanies(company),
    enableInsightsCategories: shouldEnableInsightsCategories(company),
    enableLinearCampaigns: shouldEnableLinearCampaigns(company),
    enableMetaBuying: shouldEnableMetaBuying(company),
    enableMMMNavbarGui: shouldEnableMMMNavbarGui(company),
    enableMobiusSlidesCompany: shouldEnableMobiusSlidesCompany(company),
    enableRadioBuying: shouldEnableRadioBuying(company),
    notDefaultHomeV2: shouldNotDefaultHomeV2(company),
    showCCDataMarketingInputs: shouldEnableMarketingInputsCCData(company),
    // Add new company-based experiment flags here like this:
    // myExperimentFlag: shouldEnableMyExperimentFlag(),
  };
};

export const getExperimentsForCompanyWithUserRole = (
  company: string,
  userRole: number
): Record<string, boolean> => {
  return {
    enableCreativeInsights: shouldEnableCreativeInsights(company, userRole),
    enableBrandHealthGoogle: shouldEnableBrandHealthGoogleTab(company, userRole),
    // Add new company+userRole-based experiment flags here like this:
    // myExperimentFlag: shouldEnableMyExperimentFlag(),
  };
};
